import { CalculatedPrice } from '@teko/cart/v2';
import { DiscoveryProduct } from '@teko/discovery';
import { formatCurrency } from '@teko/ds-desktop';
import { logger } from '@teko/log';
import { LIMIT_ITEM_PER_PAGE_PRODUCT_LIST } from 'constants/index';
import { convertProductDetail, ServiceContainer } from 'helpers';
import { TenantEnv } from 'libs';
import { fromRedisCache, REDIS_DEFAULT_EXPIRED } from 'server/redis-client';
import { ConvertedProduct, ProductRedisBreadCrumbs } from 'types';

const convertDiscoveryPrice = (price: ConvertedProduct['price']) => {
  if (!price) return null;
  return {
    price: price.price,
    ...(price.discount ? { discountPercent: `-${price.discount}%` } : {}),
    displayRowTotal:
      price.price && formatCurrency(price.price, { underline: true }),
    strikeThroughDisplayRowTotal:
      price.sellPrice &&
      formatCurrency(price.sellPrice, {
        underline: true,
      }),
    promotions: [],
  } as CalculatedPrice;
};

export const getServerSidePropsDetail: GetServerSideProps = async (context) => {
  try {
    const { productId, sku } = context.params || {};
    const tenantEnv = context.req.tenantEnv as TenantEnv;
    const terminalCode = context.req.terminal;
    if (!terminalCode) {
      throw new Error('no terminal');
    }
    const location = context.req.location;

    const {
      discoveryService,
      cartServiceV2,
      discoveryServiceV2,
      discoveryHelper,
    } = new ServiceContainer(tenantEnv);

    const requestParams = {
      productId: productId as string,
      sku: (sku || context.query.sku) as string,
      terminalCode,
      location,
    };

    const product = await discoveryService.getProduct(requestParams);

    if (
      product?.productInfo.canonical &&
      context.req.path !== `/${encodeURI(product.productInfo.canonical)}` &&
      !context.req.path.endsWith('.json')
    ) {
      const url = context.req.url;
      const query =
        url && url.indexOf('?') >= 0 ? url.substring(url.indexOf('?')) : '';
      return {
        props: {},
        redirect: {
          permanent: true,
          destination: `/${encodeURI(product.productInfo.canonical)}${query}`,
        },
      };
    }

    const getRelatedProducts = async () => {
      const productCategories = product?.productInfo?.categories;

      const response = await discoveryServiceV2.search(
        tenantEnv.passSkuToReturnSimilarProducts
          ? {
              page: 1,
              pageSize: LIMIT_ITEM_PER_PAGE_PRODUCT_LIST,
              filter: {
                skuToReturnSimilarProducts: sku as string,
              },
            }
          : !!productCategories?.length && !!product?.productInfo.sku
          ? {
              page: 1,
              pageSize: LIMIT_ITEM_PER_PAGE_PRODUCT_LIST,
              filter: {
                excludedSkus: [product?.productInfo.sku],
                categoryIds: [
                  productCategories[productCategories.length - 1].id,
                ],
              },
            }
          : {},
      );

      const relatedProducts = response?.products || [];

      return relatedProducts.map((product) =>
        discoveryHelper.cleanupDiscoveryV2SearchProduct(product),
      );
    };

    const getPriceAndPromotion = async () => {
      try {
        return await cartServiceV2.getPriceAndPromotion(
          (sku || context.query.sku) as string,
          {
            quantity: 1,
          },
        );
      } catch (e) {
        logger.error(context.req, e);
      }
    };

    const [relatedProducts, dataCalculatePrice, breadcrumbs] =
      await Promise.all([
        getRelatedProducts(),
        getPriceAndPromotion(),
        getBreadcrumbs(
          `${context.req.tenantEnv.platformId?.toString()}_${
            productId || product?.productDetail.productGroup?.id
          }`,
        ),
      ]);

    return {
      props: {
        serverProduct: {
          product,
          relatedProducts,
          breadcrumbs,
          priceAndPromotions:
            dataCalculatePrice ||
            convertDiscoveryPrice(
              convertProductDetail(product as DiscoveryProduct).price,
            ),
        },
        key: sku || productId,
      },
    };
  } catch (e) {
    logger.error(context.req, e);
    return {
      notFound: true,
    };
  }
};

const getBreadcrumbs = async (redisKey: string) => {
  const collection = await fromRedisCache<ProductRedisBreadCrumbs | undefined>(
    'BREADCRUMB',
    redisKey,
    async () => undefined,
    REDIS_DEFAULT_EXPIRED,
  );

  if (!collection) return [];

  const breadcrumbs = collection.breadcrumbs.map((br) => ({
    name: br.label,
    link: br.url || '#',
  }));

  return breadcrumbs;
};
